import copy from '../../../copy/en/common.json' with { type: 'json' };
import { Popover, Tooltip as RadixTooltip } from "radix-ui";
import { CircleHelp, X, type LucideIcon } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { IconButton } from "./Button.js";
import { HelpText } from "./HelpText.js";

export interface HelpPopoverProps {
  label?: string;
  children: ReactNode;
  previewOnHover?: boolean;
  icon?: LucideIcon;
}

/** Short, touch-friendly help that can sit beside a form label without nesting controls. */
export function HelpPopover({ label = copy.HelpPopover.moreInformation, children, previewOnHover = false, icon: Icon = CircleHelp }: HelpPopoverProps) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const returningFocus = useRef(false);
  const trigger = <Popover.Trigger asChild>
    <IconButton className="help-popover__trigger" icon={Icon} label={label} size="sm" tooltip={previewOnHover ? false : undefined}
      onFocus={previewOnHover ? event => {
        // Closing pinned help restores focus without reopening its preview.
        if (returningFocus.current) { returningFocus.current = false; event.preventDefault(); }
      } : undefined} />
  </Popover.Trigger>;
  return (
    <Popover.Root open={open} onOpenChange={value => { setOpen(value); setPreview(false); }}>
      {previewOnHover ? <RadixTooltip.Provider delayDuration={200}>
        <RadixTooltip.Root open={!open && preview} onOpenChange={setPreview}>
          <RadixTooltip.Trigger asChild>{trigger}</RadixTooltip.Trigger>
          <RadixTooltip.Portal><RadixTooltip.Content className="help-popover__content" sideOffset={6} collisionPadding={12}>
            <div className="help-popover__body"><HelpText>{children}</HelpText></div>
            <RadixTooltip.Arrow className="help-popover__arrow" />
          </RadixTooltip.Content></RadixTooltip.Portal>
        </RadixTooltip.Root>
      </RadixTooltip.Provider> : trigger}
      <Popover.Portal>
        <Popover.Content className="help-popover__content" aria-label={label} sideOffset={6} collisionPadding={12}
          onCloseAutoFocus={previewOnHover ? () => {
            returningFocus.current = true;
            queueMicrotask(() => { returningFocus.current = false; });
          } : undefined}>
          <div className="help-popover__body"><HelpText>{children}</HelpText></div>
          <Popover.Close asChild>
            <IconButton className="help-popover__close" icon={X} label={copy.HelpPopover.closeHelp} size="sm" tooltip={previewOnHover ? false : undefined} />
          </Popover.Close>
          <Popover.Arrow className="help-popover__arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
